import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getCashOut, deleteCashOut } from '../services/cashOutService';
import ScrollHint from '../components/ScrollHint';
import { useLocationContext } from '../contexts/LocationContext';

const CashOutDetail = () => {
  const { currentLocation } = useLocationContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const [cashOut, setCashOut] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadCashOut();
  }, [id, currentLocation?.id]);

  const loadCashOut = async () => {
    try {
      setLoading(true);
      const data = await getCashOut(id);
      setCashOut(data);
    } catch (error) {
      console.error('Error loading cash out:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this cash out? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await deleteCashOut(id);
      navigate('/');
    } catch (error) {
      console.error('Error deleting cash out:', error);
      alert('Failed to delete cash out. Please try again.');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!cashOut) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Cash out not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Link to="/cash-outs" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
            ← Back to Cash Outs
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            Cash Out - {new Date(cashOut.date + 'T12:00:00').toLocaleDateString()}
          </h1>
        </div>
        <div className="flex space-x-3">
          <Link
            to={`/cash-out/edit/${id}`}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            Edit
          </Link>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-600">Total Revenue</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">
            ${cashOut.total_revenue.toFixed(2)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-600">Cash to Deposit</div>
          <div className="text-3xl font-bold text-green-600 mt-1">
            ${cashOut.cash_to_deposit.toFixed(2)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-600">Changer Overage/Shortfall</div>
          <div className={`text-3xl font-bold mt-1 ${cashOut.changer_net_shortfall > 0 ? 'text-red-600' : 'text-green-600'}`}>
            ${(-cashOut.changer_net_shortfall).toFixed(2)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-600">Quarters Gained/Lost</div>
          <div className={`text-3xl font-bold mt-1 ${cashOut.quarters_gained_lost < 0 ? 'text-red-600' : 'text-green-600'}`}>
            {cashOut.quarters_gained_lost}
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Revenue Breakdown</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-gray-600">Self-Service</div>
            <div className="text-2xl font-bold text-blue-600">${cashOut.ss_total.toFixed(2)}</div>
            <div className="text-xs text-gray-500">
              {((cashOut.ss_total / cashOut.total_revenue) * 100).toFixed(1)}% of total
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Auto Wash</div>
            <div className="text-2xl font-bold text-green-600">${cashOut.auto_total.toFixed(2)}</div>
            <div className="text-xs text-gray-500">
              {((cashOut.auto_total / cashOut.total_revenue) * 100).toFixed(1)}% of total
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Vacuums</div>
            <div className="text-2xl font-bold text-purple-600">${cashOut.vac_total.toFixed(2)}</div>
            <div className="text-xs text-gray-500">
              {((cashOut.vac_total / cashOut.total_revenue) * 100).toFixed(1)}% of total
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Vending</div>
            <div className="text-2xl font-bold text-orange-600">${cashOut.vending_total.toFixed(2)}</div>
            <div className="text-xs text-gray-500">
              {((cashOut.vending_total / cashOut.total_revenue) * 100).toFixed(1)}% of total
            </div>
          </div>
          {(cashOut.dog_washes || []).length > 0 && (
            <div>
              <div className="text-sm text-gray-600">Dog Wash</div>
              <div className="text-2xl font-bold text-teal-600">${(cashOut.dog_total || 0).toFixed(2)}</div>
              <div className="text-xs text-gray-500">
                {cashOut.total_revenue
                  ? (((cashOut.dog_total || 0) / cashOut.total_revenue) * 100).toFixed(1)
                  : '0.0'}% of total
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm text-gray-600">SS Revenue from Coins</div>
          <div className="text-lg font-semibold">{(cashOut.ss_rev_percent_coins * 100).toFixed(2)}%</div>
        </div>
      </div>

      {/* Changers */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Bill Changers</h2>
        <ScrollHint>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Changer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coins In</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coins Out</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bills Out</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Overage/Shortfall</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {cashOut.changers.map((changer) => (
                <tr key={changer.id}>
                  <td className="px-4 py-3">Changer #{changer.changer_number}</td>
                  <td className="px-4 py-3">${changer.coins_in_dollars.toFixed(2)}</td>
                  <td className="px-4 py-3">{changer.coins_out_quarters} quarters</td>
                  <td className="px-4 py-3">${changer.bills_out_dollars.toFixed(2)}</td>
                  <td className={`px-4 py-3 font-semibold ${changer.overage_shortfall > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ${(-changer.overage_shortfall).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollHint>
      </div>

      {/* Auto Wash(es) */}
      {((cashOut.auto_washes && cashOut.auto_washes.length > 0) || cashOut.auto_wash) && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {(cashOut.auto_washes || []).length > 1 ? 'Auto Washes' : 'Auto Wash'}
          </h2>
          <div className={`${(cashOut.auto_washes || []).length > 1 ? 'space-y-4' : ''}`}>
            {(cashOut.auto_washes && cashOut.auto_washes.length > 0
              ? cashOut.auto_washes
              : cashOut.auto_wash ? [cashOut.auto_wash] : []
            ).map((aw, idx) => (
              <div key={aw.id || idx}>
                {(cashOut.auto_washes || []).length > 1 && (
                  <h3 className="font-medium text-gray-900 mb-2">Auto Wash #{aw.wash_number || idx + 1}</h3>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Bills Out</div>
                    <div className="text-lg font-semibold">${aw.bills_out_dollars.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Coins In</div>
                    <div className="text-lg font-semibold">${aw.coins_in_dollars.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Coins Out</div>
                    <div className="text-lg font-semibold">{aw.coins_out_quarters} quarters</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Revenue</div>
                    <div className="text-lg font-bold text-green-600">${aw.total_revenue.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Self-Service Bays */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Self-Service Bays
          {cashOut.ss_pooled && <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Pooled</span>}
        </h2>
        {cashOut.ss_pooled ? (
          <div className="border border-gray-200 rounded-lg p-4 max-w-md">
            <div className="font-medium text-gray-900 mb-2">All SS Bays</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Bills:</span>
                <span className="font-medium">${cashOut.ss_bays.reduce((s, b) => s + b.bills, 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Coins:</span>
                <span className="font-medium">{cashOut.ss_bays.reduce((s, b) => s + b.coins_quarters, 0)} quarters</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-600 font-medium">Total:</span>
                <span className="font-bold">${cashOut.ss_total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cashOut.ss_bays.map((bay) => (
              <div key={bay.id} className="border border-gray-200 rounded-lg p-4">
                <div className="font-medium text-gray-900 mb-2">Bay #{bay.bay_number}</div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bills:</span>
                    <span className="font-medium">${bay.bills.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Coins:</span>
                    <span className="font-medium">{bay.coins_quarters} quarters</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-gray-600 font-medium">Total:</span>
                    <span className="font-bold">${bay.total_revenue.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vacuums */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Vacuums
          {cashOut.vac_pooled && <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Pooled</span>}
        </h2>
        {cashOut.vac_pooled ? (
          <div className="border border-gray-200 rounded-lg p-4 max-w-md">
            <div className="font-medium text-gray-900 mb-2">All Vacuums</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Coins:</span>
                <span className="font-medium">{cashOut.vacuums.reduce((s, v) => s + v.coins_quarters, 0)} quarters</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-600 font-medium">Total:</span>
                <span className="font-bold">${cashOut.vac_total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {cashOut.vacuums.map((vac) => (
              <div key={vac.id} className="border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-sm font-medium text-gray-700">Vac #{vac.vacuum_number}</div>
                <div className="text-lg font-bold text-gray-900 mt-1">
                  ${vac.total_revenue.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500">{vac.coins_quarters} quarters</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vending Machines */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Vending Machines
          {cashOut.vending_pooled && <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Pooled</span>}
        </h2>
        {cashOut.vending_pooled ? (
          <div className="border border-gray-200 rounded-lg p-4 max-w-md">
            <div className="font-medium text-gray-900 mb-2">All Vending</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Bills:</span>
                <span className="font-medium">${cashOut.vending_machines.reduce((s, vm) => s + vm.bills, 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Coins:</span>
                <span className="font-medium">{cashOut.vending_machines.reduce((s, vm) => s + vm.coins_quarters, 0)} quarters</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-600 font-medium">Total:</span>
                <span className="font-bold">${cashOut.vending_total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {cashOut.vending_machines.map((vm) => (
              <div key={vm.id} className="border border-gray-200 rounded-lg p-4">
                <div className="font-medium text-gray-900 mb-2">{vm.machine_name}</div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bills:</span>
                    <span className="font-medium">${vm.bills.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Coins:</span>
                    <span className="font-medium">{vm.coins_quarters} quarters</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-gray-600 font-medium">Total:</span>
                    <span className="font-bold">${vm.total_revenue.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dog Wash */}
      {(cashOut.dog_washes || []).length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {cashOut.dog_washes.length === 1 ? 'Dog Wash' : 'Dog Washes'}
            {cashOut.dog_pooled && <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Pooled</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {cashOut.dog_washes.map((dw) => (
              <div key={dw.id} className="border border-gray-200 rounded-lg p-4">
                <div className="font-medium text-gray-900 mb-2">
                  {cashOut.dog_pooled
                    ? 'All Dog Washes'
                    : (cashOut.dog_washes.length === 1 ? 'Dog Wash' : `Dog Wash #${dw.dog_wash_number}`)}
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bills:</span>
                    <span className="font-medium">${dw.bills.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Coins:</span>
                    <span className="font-medium">{dw.coins_quarters} quarters</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-gray-600 font-medium">Total:</span>
                    <span className="font-bold">${dw.total_revenue.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CashOutDetail;
